import React, { useEffect, useState } from "react";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import "./admin.css";

interface Project {
  id: string;
  title: string;
  description: string;
  pdf_url: string;
  banner_image?: string;  // ✅ nou
  youtube_id?: string;    // ✅ nou (ID sau link, îl normalizăm la ID)
}

export const AdminProiectePage = () => {
  const [headerImage, setHeaderImage] = useState("");
  const [headerFile, setHeaderFile] = useState<File | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [newProject, setNewProject] = useState<Partial<Project>>({});
  const [newPdfFile, setNewPdfFile] = useState<File | null>(null);
  const [newBannerFile, setNewBannerFile] = useState<File | null>(null); // ✅ nou

  // încarcare proiecte + header din Firestore
  useEffect(() => {
    const fetchData = async () => {
      const docRef = doc(db, "site_content", "projects_page");
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        setHeaderImage(data.header_image || "");
        setProjects((data.projects || []) as Project[]);
      }
    };
    fetchData();
  }, []);

  // helper: extrage ID-ul YouTube dintr-un link sau întoarce stringul dacă e deja ID
  const extractYouTubeId = (input?: string): string | undefined => {
    if (!input) return undefined;
    const val = input.trim();
    // dacă e doar ID (11-12 chars frecvent), îl lăsăm
    if (/^[a-zA-Z0-9_-]{8,40}$/.test(val) && !val.includes("http")) return val;
    try {
      const u = new URL(val);
      // https://www.youtube.com/watch?v=ID
      const idByParam = u.searchParams.get("v");
      if (idByParam) return idByParam;
      // https://youtu.be/ID
      if (u.hostname.includes("youtu.be")) {
        const possibleId = u.pathname.replace("/", "");
        if (possibleId) return possibleId;
      }
      // /embed/ID
      const parts = u.pathname.split("/");
      const embedIdx = parts.findIndex((p) => p === "embed");
      if (embedIdx >= 0 && parts[embedIdx + 1]) return parts[embedIdx + 1];
    } catch {
      // nu e URL valid, returnăm așa cum e
      return val;
    }
    return val;
  };

  // încărcare imagine header (pagină)
  const uploadHeaderImage = async () => {
    if (!headerFile) return;
    const storageRef = ref(storage, `projects/header_${Date.now()}`);
    const uploadTask = uploadBytesResumable(storageRef, headerFile);
    uploadTask.on("state_changed", undefined, console.error, async () => {
      const url = await getDownloadURL(uploadTask.snapshot.ref);
      setHeaderImage(url);
    });
  };

  // upload PDF (proiect nou)
  const uploadPdf = async (): Promise<string | null> => {
    if (!newPdfFile) return null;
    const storageRef = ref(storage, `projects/pdf_${Date.now()}`);
    const uploadTask = uploadBytesResumable(storageRef, newPdfFile);
    return new Promise((resolve, reject) => {
      uploadTask.on("state_changed", undefined, reject, async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(url);
      });
    });
  };

  // upload Banner pentru proiect nou
  const uploadNewBanner = async (): Promise<string | null> => {
    if (!newBannerFile) return null;
    const storageRef = ref(storage, `projects/banner_${Date.now()}`);
    const uploadTask = uploadBytesResumable(storageRef, newBannerFile);
    return new Promise((resolve, reject) => {
      uploadTask.on("state_changed", undefined, reject, async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(url);
      });
    });
  };

  // adăugare proiect nou (cu PDF + banner + youtube)
  const addProject = async () => {
    if (!newProject.title || !newProject.description) return;

    const pdf_url = await uploadPdf();
    if (!pdf_url) return;

    const banner_image = await uploadNewBanner(); // poate fi null
    const youtube_id = extractYouTubeId(newProject.youtube_id);

    const newItem: Project = {
      id: uuidv4(),
      title: newProject.title,
      description: newProject.description,
      pdf_url,
      banner_image: banner_image || "",
      youtube_id: youtube_id || "",
    };

    setProjects((prev) => [...prev, newItem]);
    setNewProject({});
    setNewPdfFile(null);
    setNewBannerFile(null);
  };

  // ștergere proiect din listă (local)
  const deleteProject = (id: string) => {
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };

  // modifică câmp pentru un proiect existent în listă
  const updateField = <K extends keyof Project>(id: string, key: K, value: Project[K]) => {
    setProjects((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [key]: value } : p))
    );
  };

  // upload banner pentru proiect existent
  const uploadExistingBanner = async (id: string, file?: File | null) => {
    if (!file) return;
    const storageRef = ref(storage, `projects/banner_${id}_${Date.now()}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on("state_changed", undefined, console.error, async () => {
      const url = await getDownloadURL(uploadTask.snapshot.ref);
      updateField(id, "banner_image", url);
    });
  };

  // salvare în Firestore (header + toate proiectele)
  const saveAll = async () => {
    // normalizăm youtube_id pentru toate proiectele înainte de salvare
    const normalized = projects.map((p) => ({
      ...p,
      youtube_id: extractYouTubeId(p.youtube_id) || "",
    }));
    await setDoc(doc(db, "site_content", "projects_page"), {
      header_image: headerImage,
      projects: normalized,
    });
    alert("Proiectele au fost salvate cu succes!");
  };

  return (
    <div className="admin-container">
      <h1>Administrare Proiecte</h1>

      {/* HEADER BANNER (pagina Proiecte) */}
      <label>Imagine Banner (pagină):</label>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => setHeaderFile(e.target.files?.[0] || null)}
      />
      <button onClick={uploadHeaderImage}>Încarcă imagine</button>
      {headerImage && (
        <img src={headerImage} alt="Header" className="preview-img" />
      )}

      <h2>Proiecte existente</h2>
      {projects.map((p) => (
        <div key={p.id} className="project-item">
          {/* banner proiect (preview) */}
          {p.banner_image ? (
            <img
              src={p.banner_image}
              alt="Banner proiect"
              className="preview-img"
              style={{ maxWidth: 320 }}
            />
          ) : (
            <div style={{ fontSize: 12, color: "#666" }}>Fără banner</div>
          )}

          <div className="grid-2">
            <label>
              Titlu
              <input
                value={p.title}
                onChange={(e) => updateField(p.id, "title", e.target.value)}
              />
            </label>

            <label>
              YouTube (ID sau link)
              <input
                value={p.youtube_id || ""}
                onChange={(e) => updateField(p.id, "youtube_id", e.target.value)}
                placeholder="ex: https://youtu.be/abcd1234 sau abcd1234"
              />
            </label>
          </div>

          <label>
            Descriere
            <textarea
              value={p.description}
              onChange={(e) => updateField(p.id, "description", e.target.value)}
            />
          </label>

          <div className="grid-2">
            <label>
              PDF URL
              <input
                value={p.pdf_url}
                onChange={(e) => updateField(p.id, "pdf_url", e.target.value)}
              />
            </label>

            <label>
              Schimbă banner proiect
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  uploadExistingBanner(p.id, e.target.files?.[0] || null)
                }
              />
            </label>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => deleteProject(p.id)}>Șterge</button>
            {/* Nu avem „salvare individuală”; „Salvează tot” persistă tot array-ul */}
          </div>
        </div>
      ))}

      <hr />

      <h2>Adaugă proiect nou</h2>
      <input
        type="text"
        placeholder="Titlu"
        value={newProject.title || ""}
        onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
      />
      <textarea
        placeholder="Descriere"
        value={newProject.description || ""}
        onChange={(e) =>
          setNewProject({ ...newProject, description: e.target.value })
        }
      />

      <div className="grid-2">
        <div>
          <label>PDF proiect</label>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setNewPdfFile(e.target.files?.[0] || null)}
          />
        </div>

        <div>
          <label>Banner proiect (imagine)</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setNewBannerFile(e.target.files?.[0] || null)}
          />
        </div>
      </div>

      <label>YouTube (ID sau link)</label>
      <input
        type="text"
        placeholder="ex: https://youtu.be/abcd1234 sau abcd1234"
        value={newProject.youtube_id || ""}
        onChange={(e) =>
          setNewProject({ ...newProject, youtube_id: e.target.value })
        }
      />

      <button onClick={addProject}>Adaugă</button>

      <hr />
      <button className="save-btn" onClick={saveAll}>
        Salvează tot
      </button>
    </div>
  );
};